/**
 * Reference KPI Store - 참고 KPI 모달 관리
 *
 * @description 참고 KPI 검색 및 선택 기능
 *
 * @usage
 * const { openReferenceKpiModal, selectReferenceKpi } = useRefKpiStore();
 */
import { ref } from 'vue';
import { useKpiApi } from '~/composables/performance/kpi/useKpiApi';
import type { RefKpiItem, TaskGbnItem } from './useKpiTypes';

// ============================================
// 싱글톤 상태 변수
// ============================================

// 참고 KPI 모달
const showReferenceKpiModal = ref(false);
const refKpiLoading = ref(false);
const taskGbnList = ref<TaskGbnItem[]>([]);
const referenceKpiList = ref<RefKpiItem[]>([]);
const selectedTaskGbn = ref('001');
const searchRefKpiKeyword = ref('');

// ============================================
// State Export
// ============================================
export const useRefKpiState = () => {
  return {
    showReferenceKpiModal,
    refKpiLoading,
    taskGbnList,
    referenceKpiList,
    selectedTaskGbn,
    searchRefKpiKeyword,
  };
};

// ============================================
// Method Export
// ============================================
export const useRefKpiStore = () => {
  const kpiApi = useKpiApi();

  /**
   * 참고 KPI 목록 조회
   */
  const fetchReferenceKpiList = async (taskGbnId?: string, searchKeyword?: string) => {
    try {
      refKpiLoading.value = true;
      const response = await kpiApi.fetchRefKpiList(taskGbnId, searchKeyword);

      if (response) {
        if (response.taskGbnList && taskGbnList.value.length === 0) {
          taskGbnList.value = response.taskGbnList;
        }
        referenceKpiList.value = response.referenceKpiList || [];
      }
    } catch (error) {
      console.error('참고 KPI 목록 조회 실패:', error);
      referenceKpiList.value = [];
    } finally {
      refKpiLoading.value = false;
    }
  };

  /**
   * 참고 KPI 모달 열기
   */
  const openReferenceKpiModal = async () => {
    showReferenceKpiModal.value = true;
    selectedTaskGbn.value = '001';
    searchRefKpiKeyword.value = '';
    await fetchReferenceKpiList(selectedTaskGbn.value);
  };

  /**
   * 참고 KPI 모달 닫기
   */
  const closeReferenceKpiModal = () => {
    showReferenceKpiModal.value = false;
    searchRefKpiKeyword.value = '';
  };

  /**
   * 참고 KPI 검색
   */
  const searchReferenceKpi = async () => {
    await fetchReferenceKpiList(selectedTaskGbn.value, searchRefKpiKeyword.value);
  };

  /**
   * 부문 변경 핸들러
   */
  const changeTaskGbn = async (taskGbnId: string) => {
    selectedTaskGbn.value = taskGbnId;
    await fetchReferenceKpiList(taskGbnId, searchRefKpiKeyword.value);
  };

  /**
   * 참고 KPI 선택 (폼 데이터 반환)
   * @returns 선택된 참고 KPI 데이터 (kpiNm, description, unit)
   */
  const selectReferenceKpi = (refKpi: RefKpiItem) => {
    const selectedData = {
      kpiNm: refKpi.refKpiNm,
      description: refKpi.description || refKpi.actCalTypeNm || '',
      unit: refKpi.unit || '',
    };
    closeReferenceKpiModal();
    return selectedData;
  };

  return {
    // 상태
    ...useRefKpiState(),

    // 메서드
    fetchReferenceKpiList,
    openReferenceKpiModal,
    closeReferenceKpiModal,
    searchReferenceKpi,
    changeTaskGbn,
    selectReferenceKpi,
  };
};
